/**
 * Marketing attribution captured from the landing URL (FE-320).
 *
 * The browser is the only place where UTM tags and ad click ids are visible,
 * so the frontend reads them once per page load and hands them to the
 * backend (`createGuestSession`, `signInPrivy`, `touchAttribution`). First /
 * last touch, `fbc` / `fbp` and the link to the user are the server's job
 * (BE-311); nothing here is persisted beyond the tab.
 */

export const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content'
] as const

export const CLICK_ID_PARAMS = ['fbclid', 'gclid', 'ttclid'] as const

type UtmParam = (typeof UTM_PARAMS)[number]
type ClickIdParam = (typeof CLICK_ID_PARAMS)[number]

export interface Attribution {
  utm?: Partial<Record<UtmParam, string>>
  clickIds?: Partial<Record<ClickIdParam, string>>
  /** External referrer; same-origin navigations are not a referrer. */
  referrer?: string
  landingUrl: string
  /** ISO timestamp of the capture, so the server can order touches. */
  capturedAt: string
}

export interface CaptureAttributionInput {
  /** `window.location.search` */
  search: string
  /** `window.location.href` */
  href: string
  /** `window.location.origin` */
  origin: string
  /** `document.referrer` */
  referrer: string
  now?: Date
}

/** Ad platforms append long ids; anything longer is noise or abuse. */
const MAX_VALUE_LENGTH = 256
const MAX_URL_LENGTH = 2048

const cleanValue = (value: string | null) => {
  const trimmed = value?.trim() ?? ''

  return trimmed ? trimmed.slice(0, MAX_VALUE_LENGTH) : undefined
}

const pickParams = <K extends string>(
  params: URLSearchParams,
  keys: readonly K[]
): Partial<Record<K, string>> | undefined => {
  const picked: Partial<Record<K, string>> = {}

  for (const key of keys) {
    const value = cleanValue(params.get(key))
    if (value) picked[key] = value
  }

  return Object.keys(picked).length ? picked : undefined
}

const pickExternalReferrer = (referrer: string, origin: string) => {
  const value = cleanValue(referrer)
  if (!value) return undefined

  try {
    return new URL(value).origin === origin ? undefined : value
  } catch {
    return undefined
  }
}

/**
 * Returns the attribution of this page load, or `null` when the URL carries
 * no marketing tags and the visitor came directly or from the app itself —
 * the backend is never sent an empty payload.
 */
export const captureAttribution = ({
  search,
  href,
  origin,
  referrer,
  now = new Date()
}: CaptureAttributionInput): Attribution | null => {
  const params = new URLSearchParams(search)
  const utm = pickParams(params, UTM_PARAMS)
  const clickIds = pickParams(params, CLICK_ID_PARAMS)
  const externalReferrer = pickExternalReferrer(referrer, origin)

  if (!utm && !clickIds && !externalReferrer) return null

  return {
    ...(utm && { utm }),
    ...(clickIds && { clickIds }),
    ...(externalReferrer && { referrer: externalReferrer }),
    landingUrl: href.slice(0, MAX_URL_LENGTH),
    capturedAt: now.toISOString()
  }
}
